import { getItemFromStorage } from "./script"
import { parseApiDateOrDateTime } from "./dateParsing"

/** Maximum Box document upload size (150MB). Must match backend config/documents.php. */
export const MAX_BOX_UPLOAD_BYTES = 150 * 1024 * 1024

export const MAX_BOX_UPLOAD_ERROR = 'File must be 150MB or smaller.'

export const validateBoxUploadFile = (file) => {
    if (!file || typeof file.size !== 'number') {
        return null
    }

    if (file.size > MAX_BOX_UPLOAD_BYTES) {
        return MAX_BOX_UPLOAD_ERROR
    }

    return null
}

/** Split a FileList into accepted files and a user-facing error for oversized items. */
export const rejectOversizedBoxUploadFiles = (fileList) => {
    const accepted = []
    const rejectedNames = []

    for (const file of Array.from(fileList ?? [])) {
        if (validateBoxUploadFile(file)) {
            rejectedNames.push(file.name)
        } else {
            accepted.push(file)
        }
    }

    let rejectedMessage = null
    if (rejectedNames.length > 0) {
        rejectedMessage = rejectedNames.length === 1
            ? `"${rejectedNames[0]}" is too large. ${MAX_BOX_UPLOAD_ERROR}`
            : `${rejectedNames.length} file(s) are too large. ${MAX_BOX_UPLOAD_ERROR}`
    }

    return { accepted, rejectedMessage }
}

/** ECM 1 placeholder rows: count for backend flags but must not appear in document UIs. */
export const isVisibleProcoDocument = (doc) => !doc?.ecm_1_hippa && !doc?.ecm_1_lop

/** True when the document has been uploaded to Box (file_id set). */
export const documentHasFileId = (doc) => {
    const id = doc?.file_id
    return id != null && String(id).trim() !== ''
}

/** True when a case document row is linked to a lien (may not yet have a Box file). */
export const lienDocumentIsAttached = (doc) => {
    const id = doc?.id
    return id != null && String(id).trim() !== ''
}

/** Lien row side has both book and page filled. */
export const lienSideHasRecordedBookPage = (item, bookProp, pageProp) => {
    const book = item?.[bookProp]
    const page = item?.[pageProp]
    return book != null && String(book).trim() !== '' && page != null && String(page).trim() !== ''
}

/** Placeholder row: record exists but no Box file yet and not marked not-found. */
export const isPendingCaseDocument = (doc) =>
    Number(doc?.has_uploaded) === 0 && Number(doc?.not_found) === 0

export const isVisibleCaseDocument = (doc) =>
    documentHasFileId(doc) || isPendingCaseDocument(doc)

export const visibleProcoDocuments = (docs) => (docs ?? []).filter(isVisibleProcoDocument)

export const visibleCaseDocuments = (docs) =>
    (docs ?? []).filter(isVisibleCaseDocument)

export const isDeletedCaseDocument = (doc) =>
    Boolean(doc?.deleted_at || doc?.is_deleted)

export const activeCaseDocuments = (docs) =>
    visibleCaseDocuments(docs).filter((doc) => !isDeletedCaseDocument(doc))

export const deletedCaseDocuments = (docs) =>
    visibleCaseDocuments(docs).filter(isDeletedCaseDocument)

export const visibleProcoDocumentsForTab = (docs) =>
    (docs ?? []).filter((doc) => isVisibleProcoDocument(doc) && documentHasFileId(doc))

export const isDeletedProcoDocument = (doc) =>
    Boolean(doc?.deleted_at || doc?.is_deleted)

export const activeProcoDocuments = (docs) =>
    visibleProcoDocumentsForTab(docs).filter((doc) => !isDeletedProcoDocument(doc))

/** Active Proco rows for HIPAA/LOP/LOR buttons, attorney hover, and WorkQ (not deleted, has file_id). */
export const calculableProcoDocuments = (docs) =>
    visibleProcoDocumentsForTab(docs).filter((doc) => !isDeletedProcoDocument(doc))

export const deletedProcoDocuments = (docs) =>
    (docs ?? []).filter((doc) => isVisibleProcoDocument(doc) && isDeletedProcoDocument(doc))

/** Strip empty / invalid practice tags (e.g. FormData "null"). */
export const cleanDocumentPractices = (docPractices) =>
    (docPractices ?? []).filter((value) => {
        if (value == null || value === '') {
            return false
        }
        if (typeof value === 'string' && value.trim().toLowerCase() === 'null') {
            return false
        }
        return true
    })

/** Case-level docs (e.g. older LOR uploads) with no practice scope apply to the whole case. */
export const isCaseLevelDocument = (docPractices) =>
    cleanDocumentPractices(docPractices).length === 0

export const documentCoversGeneralSlot = (docPractices) => {
    const practices = cleanDocumentPractices(docPractices)
    if (practices.length === 0) {
        return true
    }
    return practices.includes('all') || practices.includes('All')
}

/**
 * Delete reason from API field or parsed from notes (latest [Deleted ...] block).
 */
export const extractDeleteReason = (doc) => {
    if (doc?.delete_reason != null && String(doc.delete_reason).trim() !== '') {
        return String(doc.delete_reason).trim()
    }

    const notes = doc?.notes
    if (notes == null || notes === '') {
        return null
    }

    const marker = '[Deleted '
    const pos = notes.lastIndexOf(marker)
    if (pos === -1) {
        return null
    }

    const block = notes.slice(pos)
    const match = block.match(/\[Deleted [^\]]+\]\s*\nReason: (.+)/s)
    return match ? match[1].trim() : null
}

export const visibleDocumentVersions = (doc) =>
    (doc?.versions ?? []).filter(documentHasFileId)

function dateToMs(value) {
    if (value == null || value === "") {
        return 0
    }
    const d = parseApiDateOrDateTime(value)
    if (!d) return 0
    const ms = d.getTime()
    return Number.isNaN(ms) ? 0 : ms
}

/** Top-level case documents only (each row is the current file; older files live under `versions`). */
export function pickLatestCaseDocumentOfType(documents, typeId) {
    if (!documents?.length || typeId == null || typeId === "") {
        return null
    }
    const tid = Number(typeId)
    const roots = documents.filter((d) =>
        Number(d.document_type_id) === tid && !isDeletedCaseDocument(d)
    )
    if (roots.length === 0) {
        return null
    }
    return roots.reduce((best, d) => {
        const created = dateToMs(d.created_at)
        const bestCreated = dateToMs(best.created_at)
        if (created !== bestCreated) {
            return created > bestCreated ? d : best
        }
        const updated = dateToMs(d.updated_at)
        const bestUpdated = dateToMs(best.updated_at)
        if (updated !== bestUpdated) {
            return updated > bestUpdated ? d : best
        }
        const id = Number(d.id) || 0
        const bestId = Number(best.id) || 0
        return id > bestId ? d : best
    })
}

function compareProcoDocumentsByRecency(a, b) {
    // const aUpdated = dateToMs(a?.updated_at)
    // const bUpdated = dateToMs(b?.updated_at)
    // if (aUpdated !== bUpdated) {
    //     return aUpdated > bUpdated ? a : b
    // }

    const aCreated = dateToMs(a?.created_at)
    const bCreated = dateToMs(b?.created_at)
    if (aCreated !== bCreated) {
        return aCreated > bCreated ? a : b
    }

    const id = Number(a?.id) || 0
    const bestId = Number(b?.id) || 0
    return id > bestId ? a : b
}

export function pickLatestProcoDocument(candidates) {
    if (!candidates?.length) {
        return null
    }

    return candidates.reduce((best, doc) => compareProcoDocumentsByRecency(doc, best), candidates[0])
}

export function resolvePracticeRowDisplayDocument(practiceSpecific, general) {
    if (!practiceSpecific) {
        return general ?? null
    }

    if (!general) {
        return practiceSpecific
    }

    return compareProcoDocumentsByRecency(practiceSpecific, general)
}

export const docsFunctions = {
    returnDocumentTypeID: (usedFor) => {
        const systemSettings = getItemFromStorage('settings')
        
        if (systemSettings && systemSettings.proco_docs_document_types) {        
            let doc = systemSettings.proco_docs_document_types.find(item => item.use_for == usedFor)
            return doc
        }
    },
    returnDocumentTypeByName: (name) => {
        const systemSettings = getItemFromStorage('settings')

        if (systemSettings && systemSettings.proco_docs_document_types && name) {
            return systemSettings.proco_docs_document_types.find(item => item.name === name) || null
        }

        return null
    },
    returnCaseDocumentTypeByName: (name) => {
        const systemSettings = getItemFromStorage('settings')

        if (systemSettings && systemSettings.document_types && name) {
            return systemSettings.document_types.find(item => item.name === name) || null
        }

        return null
    },
    getGeneralDocumentState: (docs, id, caseAttorneyId) => {
        if (!docs || !id) {
            return null
        }

        const onlyDocumentsFromAtty = calculableProcoDocuments(docs).filter(item => item.case_attorney_id == caseAttorneyId)
        const matchesType = (item) => item.procodocs_document_type_id == id
        const matches = onlyDocumentsFromAtty.filter(item => matchesType(item) && documentCoversGeneralSlot(item.practices))

        return pickLatestProcoDocument(matches)
    },
    getPracticeSpecificDocumentState: (docs, id, practiceId, caseAttorneyId) => {
        if (!docs || !id || practiceId == null || practiceId === '' || practiceId === 'all') {
            return null
        }

        const onlyDocumentsFromAtty = calculableProcoDocuments(docs).filter(item => item.case_attorney_id == caseAttorneyId)
        const matchesType = (item) => item.procodocs_document_type_id == id
        const matches = onlyDocumentsFromAtty.filter(item => matchesType(item) && item.practices?.find(practice => practice == practiceId))

        return pickLatestProcoDocument(matches)
    },
    getUploadedDocumentState: (docs, id, practiceId, caseAttorneyId) => {

        if (!docs || !id) {
            return null
        }

        if (practiceId === 'all') {
            return docsFunctions.getGeneralDocumentState(docs, id, caseAttorneyId)
        }

        return resolvePracticeRowDisplayDocument(
            docsFunctions.getPracticeSpecificDocumentState(docs, id, practiceId, caseAttorneyId),
            docsFunctions.getGeneralDocumentState(docs, id, caseAttorneyId)
        )
    },
    isDocumentGeneral: (doc, practiceId) => {
        if (doc && practiceId != 'all') {
            return documentCoversGeneralSlot(doc.practices)
        }

        return false
    },
    isDocumentUploaded: (activeCase, docsID, procodocsId) => {
        if (activeCase) {
            if (activeCase.documents && docsID) {
                let doc = activeCase.documents.find(item =>
                    item.document_type_id == docsID && !isDeletedCaseDocument(item)
                )
    
                if (doc) {
                    return true
                }
            }
    
            if (activeCase.procodocs_documents && procodocsId) {
                let doc = calculableProcoDocuments(activeCase.procodocs_documents).find(
                    item => item.procodocs_document_type_id == procodocsId
                )
    
                if (doc) {
                    return true
                }
            }
        }
    
        return false
    }
}
